import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { ActionCode } from '../constants/actions.constants';

export const actionGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    router.navigate(['/auth/login']);
    return false;
  }

  const requiredAction = route.data['requiredAction'] as ActionCode;
  const requiredActions = route.data['requiredActions'] as ActionCode[];
  const anyOfActions = route.data['anyOfActions'] as ActionCode[];

  if (!requiredAction && !requiredActions && !anyOfActions) {
    return true;
  }

  if (requiredAction) {
    if (!authService.hasAction(requiredAction)) {
      router.navigate(['/crm/unauthorized']);
      return false;
    }
  }

  if (requiredActions && requiredActions.length > 0) {
    const hasAllActions = requiredActions.every(action => authService.hasAction(action));
    if (!hasAllActions) {
      router.navigate(['/crm/unauthorized']);
      return false;
    }
  }

  if (anyOfActions && anyOfActions.length > 0) {
    const hasAnyAction = anyOfActions.some(action => authService.hasAction(action));
    if (!hasAnyAction) {
      router.navigate(['/crm/unauthorized']);
      return false;
    }
  }

  return true;
};


export function createActionGuard(requiredAction: ActionCode): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    if (!authService.isAuthenticated()) {
      router.navigate(['/auth/login']);
      return false;
    }
    if (!authService.hasAction(requiredAction)) {
      router.navigate(['/crm/unauthorized']);
      return false;
    }
    return true;
  };
}

export function createMultiActionGuard(requiredActions: ActionCode[]): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    if (!authService.isAuthenticated()) {
      router.navigate(['/auth/login']);
      return false;
    }
    const hasAllActions = requiredActions.every(action => authService.hasAction(action));
    if (!hasAllActions) {
      router.navigate(['/crm/unauthorized']);
      return false;
    }
    return true;
  };
}


export function createConditionalActionGuard(anyOfActions: ActionCode[]): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    if (!authService.isAuthenticated()) {
      router.navigate(['/auth/login']);
      return false;
    }
    const hasAnyAction = anyOfActions.some(action => authService.hasAction(action));
    if (!hasAnyAction) {
      router.navigate(['/crm/unauthorized']);
      return false;
    }

    return true;
  };
}
